import {
  BetaMenuTriggerItem,
  Spinner,
  Tooltip,
  TooltipFocusableTrigger,
  VisuallyHidden,
} from "@zapier/design-system";

import { Icon, IconName } from "@zapier/zinnia-icons";

import styles from "./KebabMenu.module.css";
import { useRouter } from "next/navigation";

export interface KebabMenuItemProps {
  /** When false, menu stays open after this item is selected. */
  shouldCloseOnSelect?: boolean;
  disabled?: boolean;
  hasWarning?: boolean;
  href?: string;
  label: string;
  loading?: boolean;
  onClick?: () => void;
  tooltipText?: string;
  icon?: IconName;
}

const KebabMenuItem = ({
  shouldCloseOnSelect,
  disabled,
  hasWarning,
  href,
  label,
  loading,
  onClick,
  tooltipText,
  icon,
}: KebabMenuItemProps) => {
  const router = useRouter();

  const handleAction = () => {
    if (disabled || loading) {
      return;
    }
    onClick?.();
    if (href) {
      router.push(href);
    }
  };

  const menuItemContent = (
    <span className={styles.menuItemContent}>
      {loading ? (
        <Spinner size="small" />
      ) : (
        !!icon && (
          <Icon
            name={icon}
            color={disabled ? "GrayWarm5" : "GrayWarm6"}
            size="20"
          />
        )
      )}
      {label}
      {/*
       * Disabled menu items stay in the a11y tree but never take focus, so a
       * hover tooltip can't reach a screen reader user — fold the reason into
       * the item's accessible name instead.
       */}
      {disabled && !!tooltipText && (
        <VisuallyHidden>{tooltipText}</VisuallyHidden>
      )}
    </span>
  );

  return (
    <BetaMenuTriggerItem
      className={styles.menuItem}
      shouldCloseOnSelect={shouldCloseOnSelect}
      isDisabled={disabled || loading}
      onAction={handleAction}
      variant={hasWarning ? "danger" : undefined}
    >
      {tooltipText && disabled ? (
        <Tooltip allowMultiline={true} content={tooltipText} placement="left">
          {/*
           * This pairing only works while the repo resolves a single
           * react-aria copy (pinned exactly in the root pnpm.overrides). With
           * two copies the DS wrapper reads a different FocusableContext than
           * the Tooltip provides and receives no trigger props at all — no
           * hover, no focus, no aria-describedby — so the tooltip can never
           * open. That split has regressed before; the wired-handlers test on
           * this component is the tripwire. The className stretches the
           * trigger to the item's full hit area so hovering anywhere on the
           * item shows the tooltip.
           */}
          <TooltipFocusableTrigger className={styles.menuItemTooltipTrigger}>
            {menuItemContent}
          </TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        menuItemContent
      )}
    </BetaMenuTriggerItem>
  );
};

export default KebabMenuItem;
